import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

type SearchContextType = {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
};

export const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'search' } }));
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Listen for other sidebars opening
  React.useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'search') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  return (
    <SearchContext.Provider value={{ sidebarOpen, toggleSidebar, openSidebar, closeSidebar }}>
      {children}
    </SearchContext.Provider>
  );
};

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
